<template>
  <v-card outlined class="modern-planner-card elevation-0 overflow-hidden" :loading="loading">
    <!-- Premium Header Area -->
    <v-toolbar flat :color="dragEnable ? 'primary' : 'secondary lighten-4'" dark height="70" class="shadow-sm">
      <v-avatar size="36" color="rgba(255,255,255,0.2)" class="mr-4">
        <v-icon size="18" color="white">{{ dragEnable ? 'fas fa-grip-lines' : 'fas fa-list-ul' }}</v-icon>
      </v-avatar>
      <v-toolbar-title class="font-weight-black">
        {{ dragEnable ? 'Planificación de Secuencia' : 'Listado de Tareas' }} 
        <span class="font-weight-medium opacity-80 pl-1">| Area: {{ machine.name }}</span>
      </v-toolbar-title>
      
      <v-spacer></v-spacer>

      <div class="d-none d-md-flex gap-3 align-center mr-6">
        <v-chip v-if="machine && machine.capacity" dark color="rgba(255,255,255,0.1)" label class="font-weight-bold px-4">
          <v-icon x-small left>fas fa-bolt</v-icon>
          Cap: {{ parseInt(machine.capacity) }} {{ machine.production_unit_m2 ? 'm²' : 'un' }} / hr
        </v-chip>
        <v-chip v-if="machine && machine.hours_per_day" dark color="rgba(255,255,255,0.1)" label class="font-weight-bold px-4">
          <v-icon x-small left>fas fa-clock</v-icon>
          {{ machine.hours_per_day }}h / día
        </v-chip>
      </div>

      <div class="d-flex align-center gap-2">
        <export-machine-planner :machine="machine" :secuence="secuence" :disabled="loading" />
        <v-btn icon color="white" @click="getMachineData" :disabled="loading" title="Recargar">
          <v-icon small>fas fa-sync-alt</v-icon>
        </v-btn>
        <v-btn v-if="dragEnable" icon color="white" class="ml-2" style="background-color: rgba(255,255,255,0.2)" @click="savePlanner" :disabled="loading" :loading="saving" title="Guardar cambios">
          <v-icon small>fas fa-save</v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <!-- Metrics Bar -->
    <div class="px-6 py-4 bg-grey-light border-bottom d-flex align-center flex-wrap gap-4">
      <div class="d-flex gap-2">
        <v-chip label outlined color="primary" class="font-weight-black shadow-xs">
          <v-icon x-small left>{{ machine.production_unit_m2 ? 'fas fa-vector-square' : 'fas fa-layer-group' }}</v-icon>
          {{ (machine.production_unit_m2 ? totalOnQueue.to_complete_m2 : totalOnQueue.to_complete_qty) | number('0,0') }} {{ machine.production_unit_m2 ? 'm²' : 'un' }} Pendiente
        </v-chip>
        <v-chip label outlined color="secondary" class="font-weight-black shadow-xs">
          <v-icon x-small left>fas fa-stopwatch</v-icon>
          {{ machine.capacity ? (machine.production_unit_m2 ? Math.ceil(totalOnQueue.to_complete_m2 / machine.capacity) : Math.ceil(totalOnQueue.to_complete_qty / machine.capacity)) : 0 }} hrs estimadas
        </v-chip>
        <v-chip label outlined color="orange darken-3" class="font-weight-black shadow-xs">
          <v-icon x-small left>fas fa-calendar-day</v-icon>
          {{ (machine.capacity && machine.hours_per_day) ? (machine.production_unit_m2 ? Math.ceil(totalOnQueue.to_complete_m2 / machine.capacity / machine.hours_per_day) : Math.ceil(totalOnQueue.to_complete_qty / machine.capacity / machine.hours_per_day)) : 0 }} días laborables
        </v-chip>
      </div>

      <v-spacer></v-spacer>

      <div class="d-flex align-center gap-4 flex-grow-1 flex-md-grow-0" style="max-width: 600px;">
        <v-select
          v-if="!dragEnable"
          v-model="filterEquipment"
          :items="equipmentList"
          label="Filtrar por Equipo"
          outlined dense hide-details
          class="modern-select flex-grow-1"
          :disabled="loading"
        />
        <v-text-field 
          v-model="search"
          placeholder="Buscar OT, Cliente o Producto..." 
          prepend-inner-icon="fas fa-search"
          outlined dense hide-details
          class="modern-search-field flex-grow-1"
          :disabled="loading"
        />
        <div class="d-flex gap-2">
          <v-btn color="primary" depressed @click="addRegister" :disabled="loading" class="font-weight-bold shadow-sm">
            <v-icon small class="mr-2">fas fa-check-double</v-icon> Reportar
          </v-btn>
          <v-btn v-if="dragEnable" color="grey darken-3" dark depressed @click="asignEquipment" :disabled="loading" class="font-weight-bold shadow-sm">
            <v-icon small class="mr-2">fas fa-user-tag</v-icon> Asignar
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Interactive Workspace -->
    <v-card-text class="pa-0">
      <div v-if="loading" class="pa-6">
        <v-skeleton-loader type="table-heading, table-row@5"></v-skeleton-loader>
      </div>
      <v-data-iterator
        v-show="!loading"
        :items="dragEnable ? secuence : filteredSecuence"
        item-key="id"
        :items-per-page="-1"
        :search="search"
        @current-items="detect"
        hide-default-footer
      >
        <template v-slot:default="{ items }">
          <v-simple-table class="modern-table planner-table">
            <thead>
              <tr>
                <th width="50" class="text-center">
                  <v-checkbox v-model="allSelected" @change="toggleSelectAll" hide-details dense class="ma-0 pa-0 d-inline-block" />
                </th>
                <th width="80" class="text-center grey lighten-5 font-weight-black">Pos.</th>
                <th width="100" class="text-center grey lighten-5 font-weight-black">OT</th>
                <th width="200" class="text-left grey lighten-5 font-weight-black">Cliente</th>
                <th class="text-left grey lighten-5 font-weight-black">Producto y Proceso</th>
                <th width="140" class="text-center grey lighten-5 font-weight-black">Avance</th>
                <th width="120" class="text-right grey lighten-5 font-weight-black">Comprometida</th>
                <th width="120" class="text-right grey lighten-5 font-weight-black">Estimada</th>
              </tr>
            </thead>
            <draggable 
              :disabled="!dragEnable"
              v-model="secuence"  
              @change="secuenceChange" 
              draggable=".draggable-row" 
              tag="tbody"
              ghost-class="ghost-row"
            >
              <tr 
                v-for="(item, idx) in items" 
                :key="item.id" 
                :class="{'draggable-row': dragEnable, 'overdue-row': item.end_date > item.delivery_date, 'completed-row-dim': item.total_mts2 >= item.product_mts2}"
              >
                <td class="text-center">
                  <v-checkbox v-model="item.completed" hide-details dense class="ma-0 pa-0 d-inline-block" color="primary" />
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center">
                    <v-avatar size="24" color="grey lighten-3" class="font-weight-black caption">{{ secuence.indexOf(item) + 1 }}</v-avatar>
                    <v-icon v-if="item.reprocess" color="warning" x-small class="ml-2">fas fa-redo</v-icon>
                  </div>
                </td>
                <td class="text-center">
                  <show-work-order :work_order_id="item.work_order" class="font-weight-black primary--text text-decoration-underline caption">{{ item.work_order }}</show-work-order>
                </td>
                <td class="text-left">
                  <div class="caption font-weight-bold secondary--text text-truncate" style="max-width: 180px;">{{ item.customer_name }}</div>
                </td>
                <td class="py-3 text-left">
                  <div v-if="item.product_sku" class="caption font-weight-black primary--text opacity-70 mb-n1">[{{ item.product_sku }}]</div>
                  <div class="body-2 font-weight-medium grey--text text--darken-4 mb-1">{{ item.product_name }}</div>
                  <div class="d-flex align-center gap-2">
                    <span class="caption font-weight-black primary--text text-uppercase">{{ item.name }}</span>
                    <v-chip v-if="item.equipment_assigned" x-small label color="grey lighten-4" class="font-weight-bold grey--text text--darken-2">
                      <v-icon x-small left>fas fa-microchip</v-icon>{{ item.equipment_assigned }}
                    </v-chip>
                  </div>
                </td>
                <!-- Progress Progress Bar -->
                <td>
                  <v-tooltip bottom max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" class="pt-1">
                        <div class="d-flex justify-space-between caption font-weight-bold mb-1">
                          <span v-if="machine.production_unit_m2">{{ item.total_mts2 | number('0.0') }} / {{ (item.product_mts2 * (item.qty_pp || 1)) | number('0.0') }}</span>
                          <span v-else>{{ item.total_qty | number('0.0') }} / {{ (item.product_qty * (item.qty_pp || 1) + (item.merma || 0)) | number('0.0') }}</span>
                        </div>
                        <v-progress-linear
                          :color="item.total_mts2 >= item.product_mts2 ? 'success' : 'primary'"
                          height="6"
                          rounded
                          :value="machine.production_unit_m2 ? (item.total_mts2 / (item.product_mts2 * (item.qty_pp || 1)) * 100) : (item.total_qty / (item.product_qty * (item.qty_pp || 1) + (item.merma || 0)) * 100)"
                        />
                      </div>
                    </template>
                    <bitacora-details-planner :process="item" />
                  </v-tooltip>
                </td>
                <!-- Dates -->
                <td class="text-right">
                  <div class="caption font-weight-bold">{{ item.delivery_date | moment("D/MM/YYYY") }}</div>
                </td>
                <td class="text-right">
                  <div :class="item.end_date > item.delivery_date ? 'error--text font-weight-black' : 'success--text font-weight-bold'" class="caption d-flex align-center justify-end">
                    <v-icon v-if="item.end_date > item.delivery_date" x-small class="mr-1">fas fa-exclamation-triangle</v-icon>
                    {{ item.end_date | moment("D/MM/YYYY") }}
                  </div>
                </td>
              </tr>
            </draggable>
          </v-simple-table>
        </template>
      </v-data-iterator>
    </v-card-text>

    <!-- Modals -->
    <v-dialog v-model="showCompleted" :max-width="machine.hour_control ? 1200 : 900" persistent transition="dialog-bottom-transition">
      <v-card outlined class="modern-card overflow-hidden">
        <process-complete 
          :processes="completed" 
          :machine="machine"
          @refresh="getMachineData"
          @close="showCompleted = false"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAsignEquipment" max-width="600" persistent transition="dialog-bottom-transition">
      <v-card outlined class="modern-card overflow-hidden">
        <asign-equipment 
          :processes="toAsignEquipment" 
          :machine="machine"
          @refresh="getMachineData"
          @close="showAsignEquipment = false"
        />
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import draggable from 'vuedraggable'
import ProcessComplete from '@/components/production/ProcessComplete'
import BitacoraDetailsPlanner from '@/components/production/BitacoraDetailsPlanner'
import ExportMachinePlanner from '@/components/production/ExportMachinePlanner'
import { updatePlanner } from '@/api/production'
import { fetchPlannerByMachine } from '@/api/reports'
import notifications from '../../utils/notifications'
import AsignEquipment from '@/components/production/AsignEquipment'

export default {
  name: 'MachinePlannerAlone',
  components: { draggable, ProcessComplete, BitacoraDetailsPlanner, ExportMachinePlanner, AsignEquipment },
  props: ['machine_id', 'mustSave', 'dragEnable'],
  data() {
    return {
      loading: false,
      saving: false,
      secuence: [],
      currentItems: [],
      search: '',
      machine: { name: '', equipments: [] },
      allSelected: false,
      showCompleted: false,
      showAsignEquipment: false,
      toAsignEquipment: [],
      completed: [],
      filterEquipment: 'Todos',
    }
  },
  methods: {
    detect(value) { this.currentItems = value },
    async savePlanner() {
      this.saving = true
      try {
        this.machine.process_orders = this.secuence
        await updatePlanner([this.machine])
        notifications.showSuccess(`Planificación de ${this.machine.name} actualizada`)
        await this.getMachineData()
      } catch (e) {
        notifications.showError('Error al guardar: ' + e)
      } finally {
        this.saving = false
      }
    },
    estimar() {
      if (!this.machine || !this.machine.capacity) return
      const { capacity, hours_per_day, production_unit_m2, saturday, sunday } = this.machine
      
      let minutes_qty = 0
      this.secuence.forEach(process => {
        const mts2 = (process.product_mts2 * (process.qty_pp || 1)) - process.total_mts2
        const qty = (process.product_qty * (process.qty_pp || 1) + (process.merma || 0)) - process.total_qty
        
        const estimated_hours = production_unit_m2 ? (mts2 > 0 ? mts2 / capacity : 0) : (qty > 0 ? qty / capacity : 0)
        const estimated_minutes = (estimated_hours / hours_per_day) * 24 * 60
        
        minutes_qty += estimated_minutes
        let end_date = new Date(new Date().setMinutes(new Date().getMinutes() + minutes_qty))

        if (end_date.getDay() === 6 || end_date.getDay() === 0) {
          if (!saturday) minutes_qty += 24 * 60
          if (!sunday) minutes_qty += 24 * 60
          end_date = new Date(new Date().setMinutes(new Date().getMinutes() + minutes_qty))
        }

        this.$set(process, 'estimated_hours', estimated_hours)
        this.$set(process, 'end_date', end_date.toISOString())
      })
    },
    secuenceChange() {
      this.estimar()
      this.$emit('update:secuence', this.secuence)
    },
    toggleSelectAll() {
      this.currentItems.forEach(s => { s.completed = this.allSelected })
    },
    addRegister() {
      this.completed = this.secuence.filter(proc => proc.completed)
      if (this.completed.length === 0) {
        notifications.showInfo('Seleccione al menos un proceso para reportar')
        return
      }
      this.completed.forEach(proc => {
        proc.real_mts2 = 0
        proc.real_qty = 0
        proc.ask_completed = false
      })
      this.showCompleted = true
    },
    asignEquipment() {
      this.toAsignEquipment = this.secuence.filter(proc => proc.completed)
      if (this.toAsignEquipment.length === 0) {
        notifications.showInfo('Seleccione uno o varios procesos para asignar equipo')
        return
      }
      this.showAsignEquipment = true
    },
    async getMachineData() {
      if (!this.machine_id) return
      this.loading = true
      try {
        const res = await fetchPlannerByMachine(`machine=${this.machine_id}`)
        this.machine = res
        this.secuence = []
        this.machine.secuence.forEach(item => {
          const process = this.machine.process_orders.find(p => p.process === item)
          if (process) this.secuence.push(process)
        })
        this.estimar()
      } catch (err) {
        notifications.showError('Error al cargar datos del área: ' + err)
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    if (this.machine_id) this.getMachineData()
  },
  watch: {
    machine_id() { this.getMachineData() },
    secuence: {
      handler() { this.estimar() },
      deep: false
    }
  },
  computed: {
    totalOnQueue() {
      const mts2 = this.secuence.reduce((t, i) => t + parseFloat(i.product_mts2 || 0) * (i.qty_pp || 1), 0)
      const qty = this.secuence.reduce((t, i) => t + (parseFloat(i.product_qty || 0) * (i.qty_pp || 1) + (i.merma || 0)), 0)
      const mts2_completed = this.secuence.reduce((t, i) => t + parseFloat(i.total_mts2 || 0), 0)
      const qty_completed = this.secuence.reduce((t, i) => t + parseFloat(i.total_qty || 0), 0)
      return { to_complete_m2: mts2 - mts2_completed, to_complete_qty: qty - qty_completed } 
    },
    equipmentList() {
      return this.machine && this.machine.equipments ? ['Todos', ...this.machine.equipments.map(i => i.name)] : ['Todos']
    },
    filteredSecuence() {
      return this.secuence.filter(i => this.filterEquipment === 'Todos' || i.equipment_assigned === this.filterEquipment)
    }
  }
}
</script>

<style scoped>
.modern-planner-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 16px !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.03) !important; }
.bg-grey-light { background-color: #f8f9fa; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.opacity-20 { background-color: rgba(255,255,255,0.2) !important; }
.opacity-10 { background-color: rgba(255,255,255,0.1) !important; }
.opacity-80 { opacity: 0.8; }
.opacity-70 { opacity: 0.7; }
.gap-2 { gap: 8px; }
.gap-4 { gap: 16px; }

/* Table Styles */
.planner-table >>> th { padding: 12px 16px !important; font-size: 0.75rem !important; letter-spacing: 0.5px; }
.planner-table >>> td { padding: 8px 16px !important; border-bottom: 1px solid rgba(0,0,0,0.04) !important; }
.overdue-row { background-color: rgba(255, 82, 82, 0.05); }
.completed-row-dim { opacity: 0.5; background-color: #f5f5f5; }
.draggable-row { cursor: grab; transition: background 0.2s; }
.draggable-row:active { cursor: grabbing; }
.draggable-row:hover { background-color: #fafafa; }
.ghost-row { opacity: 0.4; background: #e3f2fd; }
.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar { width: 8px; height: 8px; }
.modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 10px; }
</style>